import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      localStorage.setItem('adminToken', response.data.access_token);
      toast.success(isLogin ? 'Login successful!' : 'Account created successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Invalid credentials');
      } else if (error.response?.status === 400) {
        toast.error('Username already exists');
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#003366] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl font-['Outfit']">C</span>
          </div>
          <h1 className="text-3xl font-semibold text-[#1D1D1F] font-['Outfit']">
            Admin Portal
          </h1>
          <p className="text-[#86868B] mt-2">
            {isLogin ? 'Sign in to manage content' : 'Create a new admin account'}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-[#E5E5EA]">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  data-testid="admin-username-input"
                  placeholder="Enter username"
                  className="w-full bg-[#F5F5F7] border-none rounded-xl pl-12 pr-6 py-4 text-[#1D1D1F] placeholder-[#86868B] focus:ring-2 focus:ring-[#007AFF] transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#1D1D1F] font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868B]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="admin-password-input"
                  placeholder="Enter password"
                  className="w-full bg-[#F5F5F7] border-none rounded-xl pl-12 pr-12 py-4 text-[#1D1D1F] placeholder-[#86868B] focus:ring-2 focus:ring-[#007AFF] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit-btn"
              className="w-full bg-[#007AFF] text-white hover:bg-[#0062CC] rounded-xl py-4 text-lg font-medium transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-[#007AFF] font-medium hover:underline"
            >
              {isLogin ? 'Need an account? Register' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        <p className="text-center text-[#86868B] text-sm mt-6">
          <a href="/" className="hover:text-[#007AFF]">← Back to website</a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
